import React, { useState, useEffect, useRef, useCallback } from "react";
import { Bell } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import NotificationList from "./NotificationList";
import { log } from "../lib/log";

export const NOTIFY_REFRESH_EVENT = "fmf:notify-refresh";
const POLL_MS = 60000;

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, onOutside]);
}

function useNotifications(user) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      await api.checkNotifications();
      const d = await api.listNotifications();
      setItems(d.items);
      setUnread(d.unread);
    } catch (err) {
      log.warn("Could not refresh notifications", err?.response?.status);
    }
  }, []);

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return undefined; }
    load();
    const t = setInterval(load, POLL_MS);
    window.addEventListener(NOTIFY_REFRESH_EVENT, load);
    return () => { clearInterval(t); window.removeEventListener(NOTIFY_REFRESH_EVENT, load); };
  }, [user, load]);

  const markRead = useCallback(async () => {
    try {
      await api.markNotificationsRead();
    } catch (err) {
      log.error("Could not mark notifications as read", err);
    }
    setUnread(0);
    setItems((it) => it.map((x) => ({ ...x, read: true })));
    window.dispatchEvent(new Event(NOTIFY_REFRESH_EVENT));
  }, []);

  return { items, unread, markRead };
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close);
  const { items, unread, markRead } = useNotifications(user);

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} aria-label="Notifications" data-testid="notification-bell" className={`relative flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${open ? "border-blue-200 bg-blue-50 text-blue-600" : "border-slate-200 text-slate-500 hover:text-blue-600"}`}>
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notification-unread-badge" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unread > 9 ? "9+" : unread}</span>
        )}
      </button>
      {open && <NotificationList items={items} unread={unread} onMarkRead={markRead} onOpen={close} isPro={Boolean(user.pro)} />}
    </div>
  );
}
